import React, { useState, useEffect } from 'react';
import { useAppStore } from '../state/store';

const InstrumentSelector: React.FC = () => {
  const instruments = useAppStore((s) => s.instruments);
  const primaryInstrumentId = useAppStore((s) => s.primaryInstrumentId);
  const addInstrument = useAppStore((s) => s.addInstrument);
  const updateInstrument = useAppStore((s) => s.updateInstrument);
  const removeInstrument = useAppStore((s) => s.removeInstrument);

  const primary = instruments.find((i) => i.id === primaryInstrumentId);
  const additional = instruments.filter((i) => i.id !== primaryInstrumentId);

  const [primarySymbol, setPrimarySymbol] = useState(primary?.symbol ?? '');
  const [primaryAdjusted, setPrimaryAdjusted] = useState(primary?.adjusted ?? true);

  useEffect(() => {
    setPrimarySymbol(primary?.symbol ?? '');
    setPrimaryAdjusted(primary?.adjusted ?? true);
  }, [primary?.id, primary?.symbol, primary?.adjusted]);

  const [addingSymbol, setAddingSymbol] = useState('');
  const [addingAdjusted, setAddingAdjusted] = useState(true);
  const [showAddRow, setShowAddRow] = useState(false);

  const handlePrimarySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const symbol = primarySymbol.trim();
    if (!symbol) return;
    if (primaryInstrumentId) {
      updateInstrument(primaryInstrumentId, { symbol, adjusted: primaryAdjusted });
    } else {
      addInstrument(symbol, primaryAdjusted);
    }
  };

  const handleAddAdditional = (e: React.FormEvent) => {
    e.preventDefault();
    const symbol = addingSymbol.trim();
    if (!symbol) return;
    addInstrument(symbol, addingAdjusted, { setAsPrimary: false });
    setAddingSymbol('');
    setAddingAdjusted(true);
    setShowAddRow(false);
  };

  const primarySymbolInput = (
    <input
      id="instrument-primary-symbol"
      type="text"
      value={primarySymbol}
      onChange={(e) => setPrimarySymbol(e.target.value)}
      placeholder="e.g. SPY or ^VIX"
      className="border-2 border-rule rounded-sm bg-newsprint-panel text-ink px-3 py-2 min-w-[10rem] focus:outline-none focus:border-ink focus:ring-4 focus:ring-newsprint-dark"
    />
  );

  const adjustedToggle = (
    isAdjusted: boolean,
    setAdjusted: (v: boolean) => void,
    ariaLabel: string
  ) => (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className="inline-flex rounded-sm border border-rule bg-newsprint-dark p-0.5"
    >
      {(['adjusted', 'unadjusted'] as const).map((mode) => {
        const val = mode === 'adjusted';
        return (
          <button
            key={mode}
            type="button"
            role="radio"
            aria-checked={isAdjusted === val}
            onClick={() => setAdjusted(val)}
            className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
              isAdjusted === val ? 'bg-ink text-newsprint' : 'text-ink hover:bg-newsprint'
            }`}
          >
            {mode === 'adjusted' ? 'Adjusted' : 'Unadjusted'}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-6">
      <form onSubmit={handlePrimarySubmit} className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <label htmlFor="instrument-primary-symbol" className="text-sm font-medium text-ink">
            Primary symbol:
          </label>
          {primarySymbolInput}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-ink">Prices:</span>
          {adjustedToggle(primaryAdjusted, setPrimaryAdjusted, 'Primary adjusted or unadjusted')}
        </div>
        <button
          type="submit"
          className="bg-ink text-newsprint px-4 py-2 rounded-sm hover:opacity-90"
        >
          {primaryInstrumentId ? 'Update primary' : 'Set primary'}
        </button>
      </form>

      {primaryInstrumentId !== null && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-ink font-headline">Additional instruments</h3>
          <div className="space-y-2">
            {additional.map((inst) => (
              <AdditionalInstrumentRow
                key={inst.id}
                symbol={inst.symbol}
                adjusted={inst.adjusted}
                onUpdate={(updates) => updateInstrument(inst.id, updates)}
                onRemove={() => removeInstrument(inst.id)}
                adjustedToggle={adjustedToggle}
              />
            ))}
            {showAddRow ? (
              <form
                onSubmit={handleAddAdditional}
                className="flex flex-wrap items-center gap-3 py-2"
              >
                <input
                  type="text"
                  value={addingSymbol}
                  onChange={(e) => setAddingSymbol(e.target.value)}
                  placeholder="e.g. ^VIX"
                  className="border-2 border-rule rounded-sm bg-newsprint-panel text-ink px-3 py-2 min-w-[8rem] focus:outline-none focus:border-ink focus:ring-4 focus:ring-newsprint-dark"
                  autoFocus
                />
                {adjustedToggle(addingAdjusted, setAddingAdjusted, 'New instrument adjusted or unadjusted')}
                <button type="submit" className="bg-ink text-newsprint px-3 py-2 rounded-sm hover:opacity-90 text-sm">
                  Add
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddRow(false);
                    setAddingSymbol('');
                  }}
                  className="px-3 py-2 rounded-sm border border-rule bg-newsprint-panel text-ink hover:bg-newsprint-dark text-sm"
                >
                  Cancel
                </button>
              </form>
            ) : (
              <button
                type="button"
                onClick={() => setShowAddRow(true)}
                className="px-4 py-2 rounded-sm border border-rule bg-newsprint-panel text-ink hover:bg-newsprint-dark text-sm"
              >
                Add additional instrument
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

type AdditionalInstrumentRowProps = {
  symbol: string;
  adjusted: boolean;
  onUpdate: (updates: { symbol?: string; adjusted?: boolean }) => void;
  onRemove: () => void;
  adjustedToggle: (
    isAdjusted: boolean,
    setAdjusted: (v: boolean) => void,
    ariaLabel: string
  ) => React.ReactNode;
};

const AdditionalInstrumentRow: React.FC<AdditionalInstrumentRowProps> = ({
  symbol,
  adjusted,
  onUpdate,
  onRemove,
  adjustedToggle,
}) => {
  const [symbolInput, setSymbolInput] = useState(symbol);

  useEffect(() => {
    setSymbolInput(symbol);
  }, [symbol]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const s = symbolInput.trim();
    if (!s) return;
    onUpdate({ symbol: s });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-wrap items-center gap-3 py-2 pl-2 border-l-2 border-rule"
    >
      <input
        type="text"
        value={symbolInput}
        onChange={(e) => setSymbolInput(e.target.value)}
        placeholder="e.g. SPY, ^VIX"
        className="border-2 border-rule rounded-sm bg-newsprint-panel text-ink px-3 py-2 min-w-[8rem] focus:outline-none focus:border-ink focus:ring-4 focus:ring-newsprint-dark"
      />
      {adjustedToggle(adjusted, (v) => onUpdate({ adjusted: v }), 'Adjusted or unadjusted')}
      <button
        type="submit"
        className="bg-ink text-newsprint px-3 py-2 rounded-sm hover:opacity-90 text-sm"
      >
        Update
      </button>
      <button
        type="button"
        onClick={onRemove}
        className="px-2 py-1 text-sm text-error border border-rule rounded-sm hover:bg-newsprint-dark"
        title="Remove instrument"
      >
        Remove
      </button>
    </form>
  );
};

export default InstrumentSelector;
